import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Spinner,
} from '@fluentui/react-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface NewChatWarningDialogProps {
    open: boolean;
    // What the customer would abandon: a live chat with an agent ('human') or a spot in the agent
    // queue ('queued'). Only the body copy differs.
    reason: 'human' | 'queued';
    // True while the old chat is being closed on the server; keeps the buttons disabled so a
    // double-click can't fire two closes / two new-chat creations.
    busy: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Warns the customer that starting a new chat abandons their current live chat (#1937): either an
 * active conversation with a support agent, or their place in the agent queue. Confirming closes
 * that chat ("user closed chat") — removing it from the agent's active list or the queue — before
 * the new one is created.
 */
export const NewChatWarningDialog: FC<NewChatWarningDialogProps> = ({ open, reason, busy, onConfirm, onCancel }) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onOpenChange={(_, data) => {
                // Dismiss (Esc / backdrop) counts as cancel; never mid-close so we don't strand a
                // pending create.
                if (!data.open && !busy) {
                    onCancel();
                }
            }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{t('newChatWarning.title')}</DialogTitle>
                    <DialogContent>
                        {reason === 'queued' ? t('newChatWarning.queuedBody') : t('newChatWarning.body')}
                    </DialogContent>
                    <DialogActions>
                        <Button appearance="secondary" disabled={busy} onClick={onCancel}>
                            {t('newChatWarning.cancel')}
                        </Button>
                        <Button
                            appearance="primary"
                            disabled={busy}
                            icon={busy ? <Spinner size="tiny" /> : undefined}
                            onClick={onConfirm}
                            data-testid="new-chat-warning-confirm"
                        >
                            {t('newChatWarning.confirm')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
